// components/ConsultationCalendar.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { CONSULTATION_TYPES, TIME_SLOTS } from '../../../data/constants';
import { generateAvailableDates, formatDate } from './helpers';

const ConsultationCalendar = ({ formData, onUpdate, onNext }) => {
  const [selectedDate, setSelectedDate] = useState(formData.preferredDate || "");
  const [selectedTime, setSelectedTime] = useState(formData.preferredTime || "");
  const [consultationType, setConsultationType] = useState(formData.consultationType || "virtual");
  const [showQuickNav, setShowQuickNav] = useState(false);
  
  const availableDates = generateAvailableDates();

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    onUpdate({ preferredDate: date });
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    onUpdate({ preferredTime: time });
  };

  const handleConsultationTypeSelect = (type) => {
    setConsultationType(type);
    onUpdate({ consultationType: type });
  };

  const handleContinue = () => {
    if (selectedDate && selectedTime && consultationType) {
      onNext();
    }
  };

  const isFormValid = selectedDate && selectedTime && consultationType;
  const selectedType = CONSULTATION_TYPES.find((t) => t.id === consultationType);

  return (
    <div>
      <div className="md:hidden sticky top-0 z-40 bg-surface border-b border-border mb-4">
        <button
          aria-haspopup="dialog"
          onClick={() => setShowQuickNav(true)}
          className="w-full p-4 border-2 border-dashed border-accent rounded-xl text-left"
        >
          <p className="text-sm text-text-secondary">
            Selected Consultation Date & Time
          </p>
          <p className="font-semibold text-primary">
            {selectedType?.title || "Tap to choose preferred meeting"}
          </p>
          <p className="text-sm text-text-secondary">
            {selectedDate || "Selected Date"} · {selectedTime || "Selected Time"}
          </p>
        </button>
      </div>
      
      {showQuickNav && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/60">
          <div className="absolute bottom-0 w-full bg-surface rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-primary">Quick Selection</h3>
              <button
                onClick={() => setShowQuickNav(false)}
                className="text-sm px-3 py-1 rounded-md hover:bg-surface"
              >
                Close
              </button>
            </div>

            <div className="mb-2 sm:mb-6">
              <p className="text-sm font-medium mb-2">Consultation Type</p>
              <div className="space-y-2">
                {CONSULTATION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleConsultationTypeSelect(type.id)}
                    className={`w-full p-3 rounded-lg border text-left ${
                      consultationType === type.id
                        ? "border-accent bg-accent/10"
                        : "border-border"
                    }`}
                  >
                    {type.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Date</p>
              <div className="grid grid-cols-3 gap-2">
                {availableDates.map((d) => (
                  <button
                    key={d.date}
                    disabled={!d.available}
                    onClick={() => handleDateSelect(d.date)}
                    className={`p-2 text-xs rounded ${
                      selectedDate === d.date
                        ? "bg-accent text-white"
                        : d.available
                          ? "bg-surface border"
                          : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {d.display}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Time</p>
              <div className="grid grid-cols-2 gap-2">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t.value}
                    disabled={!t.available}
                    onClick={() => handleTimeSelect(t.value)}
                    className={`p-2 rounded text-sm ${
                      selectedTime === t.value
                        ? "bg-accent text-white"
                        : t.available
                          ? "border"
                          : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="hidden md:block">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Calendar" size={16} strokeWidth={2} />
            <span>Schedule Consultation</span>
          </div>
          <h2 className="text-3xl font-bold text-primary mb-4">
            Let's Schedule Your Consultation
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Choose your preferred consultation format and select a convenient time.
          </p>
        </div>

        <div className="mb-12">
          <h3 className="text-xl font-bold text-primary mb-6">
            Choose Your Consultation Format
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CONSULTATION_TYPES.map((type) => (
              <div
                key={type.id}
                onClick={() => handleConsultationTypeSelect(type.id)}
                className={`p-6 border-2 rounded-xl cursor-pointer cinematic-transition hover-lift ${
                  consultationType === type.id
                    ? "border-accent bg-accent/5"
                    : "border-border hover:border-accent/50 hover:bg-surface"
                }`}
              >
                <div className="text-center mb-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                      consultationType === type.id
                        ? "bg-accent text-white"
                        : "bg-surface text-accent"
                    }`}
                  >
                    <Icon name={type.icon} size={24} strokeWidth={2} />
                  </div>
                  <h4 className="font-bold text-primary mb-2">{type.title}</h4>
                  <p className="text-sm text-text-secondary mb-3">
                    {type.description}
                  </p>
                  <div className="text-xs text-accent font-medium">
                    {type.duration}
                  </div>
                </div>

                <ul className="space-y-1">
                  {type.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center space-x-2 text-xs text-text-secondary"
                    >
                      <Icon
                        name="Check"
                        size={12}
                        strokeWidth={2}
                        className="text-accent"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {consultationType === type.id && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center">
                      <Icon
                        name="Check"
                        size={16}
                        strokeWidth={2}
                        className="text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <h3 className="text-xl font-bold text-primary mb-6">
            Select Your Preferred Date
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {availableDates.map((date) => (
              <button
                key={date.date}
                onClick={() => date.available && handleDateSelect(date.date)}
                disabled={!date.available}
                className={`p-4 rounded-lg text-center cinematic-transition ${
                  selectedDate === date.date
                    ? "bg-accent text-white"
                    : date.available
                      ? "bg-surface hover:bg-accent/10 text-primary hover:text-accent border border-border hover:border-accent"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                <div className="text-sm font-medium">{date.display}</div>
                {!date.available && (
                  <div className="text-xs mt-1">Unavailable</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {selectedDate && (
          <div className="mb-12">
            <h3 className="text-xl font-bold text-primary mb-6">
              Select Your Preferred Time
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot.value}
                  onClick={() => slot.available && handleTimeSelect(slot.value)}
                  disabled={!slot.available}
                  className={`p-4 rounded-lg text-center cinematic-transition ${
                    selectedTime === slot.value
                      ? "bg-accent text-white"
                      : slot.available
                        ? "bg-surface hover:bg-accent/10 text-primary hover:text-accent border border-border hover:border-accent"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <div className="font-medium">{slot.label}</div>
                  {!slot.available && (
                    <div className="text-xs mt-1">Booked</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {isFormValid && (
        <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-primary mb-4">
            Consultation Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Format:</span>
              <span className="font-medium text-primary">
                {selectedType?.title}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Date:</span>
              <span className="font-medium text-primary">
                {formatDate(selectedDate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Time:</span>
              <span className="font-medium text-primary">
                {TIME_SLOTS.find((t) => t.value === selectedTime)?.label}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Duration:</span>
              <span className="font-medium text-primary">
                {selectedType?.duration}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!isFormValid}
          className={`flex items-center space-x-2 text-sm px-3 sm:px-8 py-2 sm:py-4 rounded-lg font-semibold cinematic-transition ${
            isFormValid
              ? "bg-red-700 hover:bg-accent-dark text-white cta-glow hover:shadow-elevation"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          <span>Continue to Investment Discussion</span>
          <Icon name="ArrowRight" size={20} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default ConsultationCalendar;